/* Locked In scroll layer. No libraries. Every effect is an enhancement: without this file the page is
   complete and still, and reduced-motion visitors get fades only (html.nx-gentle).
   1. Wall lettering applies word by word when a wall comes into view [data-nx-words]
   2. Pinned exhibit: scroll steps through an object's views [data-nx-steps]
   3. Film: a pinned wall where scroll drives a short film and the lettering around it [data-nx-film]
   4. Sweep: a room pins and its objects slide past sideways [data-nx-sweep]
   5. Scroll cue: a line under the header, painted in the colour of the wall you are in front of
   6. Filtered grids: new product cards settle in instead of popping */
const html = document.documentElement;
const desk = matchMedia('(min-width: 990px)');
const moving = () => html.classList.contains('nx-motion');
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const wrap = () => document.querySelector('.page-wrapper');
const scrollers = () => [window, wrap()].filter(Boolean);
const useWrap = () => { const s = wrap(); return desk.matches && s && s.scrollHeight > s.clientHeight + 4 ? s : null; };
const scrollByY = (y) => { const s = useWrap(); const o = { top: y, behavior: moving() ? 'smooth' : 'auto' }; if (s) s.scrollBy(o); else window.scrollBy(o); };

/* One measurement pass per frame. If frames are paused (background tab, power saving) a short timer
   stands in so state never goes stale. */
let ticking = false;
const jobs = new Set();
const onFrame = (fn) => jobs.add(fn);
function tick() { if (!ticking) return; ticking = false; jobs.forEach((fn) => fn()); }
function request() { if (ticking) return; ticking = true; requestAnimationFrame(tick); setTimeout(tick, 64); }
scrollers().forEach((s) => s.addEventListener('scroll', request, { passive: true }));
addEventListener('resize', request, { passive: true });

/* Progress through a pinned section: 0 when its top meets the pin line, 1 when its end leaves */
function pinProgress(sec, pin) {
  const travel = sec.getBoundingClientRect().height - (pin ? pin.offsetHeight : innerHeight);
  const top = sec.getBoundingClientRect().top - (pin ? parseFloat(getComputedStyle(pin).top) || 0 : 0);
  return { p: travel > 0 ? clamp(-top / travel) : 0, travel };
}

/* 1. Wall lettering */
function initWords(scope = document) {
  const els = scope.querySelectorAll('[data-nx-words]:not([data-nx-words-on])');
  if (!els.length) return;
  if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
  els.forEach((el) => { el.setAttribute('data-nx-words-on', ''); io.observe(el); });
}

/* 2. Pinned exhibit stepper */
function initSteps(scope = document) {
  scope.querySelectorAll('[data-nx-steps]:not([data-nx-steps-on])').forEach((sec) => {
    const views = [...sec.querySelectorAll('[data-nx-view]')];
    const ticks = [...sec.querySelectorAll('[data-nx-tick]')];
    if (views.length < 2) return;
    sec.setAttribute('data-nx-steps-on', '');
    sec.style.setProperty('--steps', views.length);
    const pin = sec.querySelector('[data-nx-pin]');
    let current = -1;
    const show = (i) => {
      if (i === current) return;
      current = i;
      views.forEach((v, n) => v.classList.toggle('is-on', n === i));
      ticks.forEach((t, n) => { t.classList.toggle('is-on', n === i); t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
    };
    ticks.forEach((t, n) => t.addEventListener('click', () => {
      const { travel } = pinProgress(sec, pin);
      scrollByY(sec.getBoundingClientRect().top + travel * ((n + 0.5) / views.length));
    }));
    onFrame(() => {
      const { p } = pinProgress(sec, pin);
      sec.style.setProperty('--p', p.toFixed(4));
      show(Math.min(views.length - 1, Math.floor(p * views.length)));
    });
    show(0);
  });
  request();
}

/* 3. Film: scroll position drives the film's playhead, the lettering and the captions */
function initFilm(scope = document) {
  scope.querySelectorAll('[data-nx-film]:not([data-nx-film-on])').forEach((sec) => {
    if (!moving()) return;
    sec.setAttribute('data-nx-film-on', '');
    const pin = sec.querySelector('[data-nx-pin]');
    const video = sec.querySelector('video');
    const beats = [...sec.querySelectorAll('[data-nx-beat]')];
    let want = 0, have = 0, pending = false, beat = -1;
    if (video) {
      video.pause();
      video.removeAttribute('autoplay');
      const ready = () => sec.classList.add('is-ready');
      if (video.readyState >= 2) ready(); else video.addEventListener('loadeddata', ready, { once: true });
      if (video.preload !== 'auto') { video.preload = 'auto'; video.load(); }
    }
    /* Ease the playhead towards the scroll position; a timer stands in when frames are paused */
    const kick = () => {
      if (pending) return;
      pending = true;
      const run = () => { if (!pending) return; pending = false; seek(); };
      requestAnimationFrame(run);
      setTimeout(run, 50);
    };
    const seek = () => {
      if (!video || !video.duration) return;
      have += (want - have) * 0.25;
      if (Math.abs(want - have) < 0.004) have = want;
      const t = have * (video.duration - 0.05);
      if (Math.abs(video.currentTime - t) > 1 / 60 && !video.seeking) video.currentTime = t;
      if (have !== want || video.seeking) kick();
    };
    onFrame(() => {
      const { p } = pinProgress(sec, pin);
      sec.style.setProperty('--p', p.toFixed(4));
      want = clamp((p - 0.04) / 0.82);
      kick();
      const b = beats.length ? Math.min(beats.length - 1, Math.floor(clamp(p / 0.9) * beats.length)) : -1;
      if (b !== beat) { beat = b; beats.forEach((el, n) => el.classList.toggle('is-on', n === b)); }
      sec.classList.toggle('is-end', p > 0.86);
    });
  });
  request();
}

/* 3b. Generic pinned progress: exposes --p (0 to 1) on any [data-nx-progress] section for CSS to use */
function initProgress(scope = document) {
  scope.querySelectorAll('[data-nx-progress]:not([data-nx-progress-on])').forEach((sec) => {
    if (!moving()) return;
    sec.setAttribute('data-nx-progress-on', '');
    const pin = sec.querySelector('[data-nx-pin]');
    onFrame(() => { sec.style.setProperty('--p', pinProgress(sec, pin).p.toFixed(4)); });
  });
  request();
}

/* 3c. Kinetic room bands: each band's lettering runs sideways with scroll, alternate bands in opposite directions.
   Script-driven so it works in every browser (not only those with CSS scroll timelines). */
function initKinetic(scope = document) {
  if (!moving()) return;
  const rows = [...scope.querySelectorAll('.nx-kin__row:not([data-kin-on])')];
  if (!rows.length) return;
  const items = rows.map((row) => { row.setAttribute('data-kin-on', ''); return { row, track: row.querySelector('.nx-kin__track'), dir: parseFloat(getComputedStyle(row).getPropertyValue('--dir')) || 1 }; });
  html.classList.add('nx-kin-js');
  onFrame(() => {
    const vh = innerHeight;
    items.forEach(({ row, track, dir }) => {
      const r = row.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const p = clamp((vh - r.top) / (vh + r.height));
      track.style.transform = `translate3d(${(-22 + dir * (0.5 - p) * 44).toFixed(2)}%, 0, 0)`;
    });
  });
  request();
}

/* 4. Sweep: the room's rail is driven by vertical scroll while its wall is pinned */
function initSweep(scope = document) {
  scope.querySelectorAll('[data-nx-sweep]:not([data-nx-sweep-on])').forEach((sec) => {
    if (!moving()) return;
    const rail = sec.querySelector('nx-rail');
    if (!rail) return;
    const start = () => {
      if (typeof rail.paint !== 'function') { setTimeout(start, 100); return; }
      sec.setAttribute('data-nx-sweep-on', '');
      const items = [...sec.querySelectorAll('.nx-rail__item')];
      const size = () => {
        rail.measure();
        const travel = -rail.min;
        sec.style.height = `${Math.round(rail.offsetHeight + travel * 1.1 + innerHeight * 0.25)}px`;
      };
      size();
      new ResizeObserver(size).observe(rail.querySelector('[data-rail-track]'));
      let last = null;
      onFrame(() => {
        const { p } = pinProgress(sec, rail);
        const x = Math.round(rail.min * clamp((p - 0.08) / 0.84) * 100) / 100;
        if (x !== last) { last = x; if (rail.spring) rail.spring.jump(x); rail.paint(x); }
        const edge = innerWidth * 0.96;
        items.forEach((it) => { if (it.getBoundingClientRect().left < edge) it.classList.add('is-hung'); });
      });
      request();
    };
    start();
  });
}

/* 5. Scroll cue in the current wall's paint */
function initCue() {
  const header = document.querySelector('[data-nx-header]');
  if (!header || header.querySelector('.nx-cue') || header.getAttribute('data-cue') === 'off') return;
  const cue = document.createElement('div');
  cue.className = 'nx-cue';
  cue.setAttribute('aria-hidden', 'true');
  cue.innerHTML = '<i></i>';
  header.appendChild(cue);
  const fill = cue.firstChild;
  onFrame(() => {
    const s = useWrap();
    const top = s ? s.scrollTop : scrollY;
    const max = s ? s.scrollHeight - s.clientHeight : html.scrollHeight - innerHeight;
    fill.style.transform = `scaleX(${max > 0 ? clamp(top / max).toFixed(4) : 0})`;
    const y = Math.min(innerHeight - 1, header.getBoundingClientRect().bottom + 2);
    const el = document.elementFromPoint(innerWidth / 2, y);
    const wall = el && el.closest('.nx-wall');
    const paint = wall ? getComputedStyle(wall).getPropertyValue('--wall').trim() : '';
    if (paint !== cue.dataset.paint) { cue.dataset.paint = paint; cue.style.setProperty('--cue', paint || 'var(--nx-fg)'); }
  });
  request();
}

/* 6. Filtered grids settle in */
function initGrids() {
  if (!moving() || !('animate' in Element.prototype)) return;
  const main = document.getElementById('MainContent');
  if (!main) return;
  let ready = false;
  setTimeout(() => { ready = true; }, 1500);
  new MutationObserver((list) => {
    if (!ready) return;
    let n = 0;
    list.forEach((m) => m.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      const cards = node.matches('.product-grid__item') ? [node] : [...node.querySelectorAll('.product-grid__item')];
      cards.slice(0, 24).forEach((c) => {
        c.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], { duration: 280, delay: Math.min(n++, 12) * 28, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'backwards' });
      });
    }));
  }).observe(main, { childList: true, subtree: true });
}

function boot() {
  html.classList.add('nx-scroll-on');
  initWords();
  initSteps();
  initFilm();
  initProgress();
  initKinetic();
  initSweep();
  initCue();
  initGrids();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
document.addEventListener('shopify:section:load', (e) => { initWords(e.target); initSteps(e.target); initFilm(e.target); initProgress(e.target); initSweep(e.target); });
